import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import clsx from 'clsx'
import { CheckMarkIcon } from '@svgs'
import { useGetPublicStatsQuery } from 'src/redux/api'

// The "instruction" the banners point to: the cabinet banner scrolls here
export const HOW_IT_WORKS_ID = 'how-it-works'
const BODY_ID = 'how-it-works-body'

interface IHowItWorks {
  // Shown inside a modal (listing page banner): no card of its own
  embedded?: boolean
}

// "How it works" — the three steps of the reverse-marketplace flow plus the
// platform trust points, so a newcomer instantly gets the model. On phones it
// starts as one row that opens in place: the three steps used to take a whole
// screen of the cabinet on every visit.
const HowItWorks: FC<IHowItWorks> = ({ embedded }) => {
  const { t } = useTranslation()
  const { data: stats } = useGetPublicStatsQuery()
  const isDesktop = useMediaQuery({ minWidth: 992 })
  // the footer's "Як це працює" link lands here opened
  const [expanded, setExpanded] = useState(
    () => typeof window !== 'undefined' && window.location.hash === `#${HOW_IT_WORKS_ID}`,
  )
  const collapsible = !embedded && !isDesktop
  const open = !collapsible || expanded

  const steps = [
    { title: t('home_how.step1_title'), text: t('home_how.step1_text') },
    { title: t('home_how.step2_title'), text: t('home_how.step2_text') },
    { title: t('home_how.step3_title'), text: t('home_how.step3_text') },
  ]
  const badges = [
    t('home_how.badge_moderation'),
    t('home_how.badge_diia'),
    t('home_how.badge_agreement'),
  ]

  return (
    <section
      id={embedded ? undefined : HOW_IT_WORKS_ID}
      className={clsx(
        'flex flex-col',
        embedded && 'gap-5 pt-2 pr-10',
        !embedded && 'rounded-2xl bg-surface shadow-card',
        collapsible && 'gap-4 px-4 py-3.5',
        !embedded && !collapsible && 'gap-5 p-8',
      )}
    >
      {collapsible ? (
        <h2>
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={BODY_ID}
            onClick={() => setExpanded((value) => !value)}
            className="flex items-center gap-3 w-full min-h-11 text-left"
          >
            <span className="flex items-center justify-center w-10 h-10 shrink-0 rounded-xl bg-brand-soft text-brand-ink">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 11v6M12 7.5v.5" />
              </svg>
            </span>
            <span className="flex flex-col gap-0.5 flex-1 min-w-0">
              <span className="text-[0.9375rem] leading-5 font-extrabold text-ink">
                {t('home_how.title')}
              </span>
              <span className="text-[0.8125rem] leading-[1.125rem] font-normal text-ink-2">
                {t('home_how.summary')}
              </span>
            </span>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className={clsx(
                'stroke-ink-2 shrink-0 transition-transform duration-200',
                expanded && 'rotate-90',
              )}
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </h2>
      ) : (
        <h2 className="text-xl md:text-[1.375rem] font-extrabold leading-tight text-ink">
          {t('home_how.title')}
        </h2>
      )}
      {open && (
        <div id={BODY_ID} className="flex flex-col gap-5">
          <div className="grid gap-5 md:grid-cols-3">
            {steps.map((step, index) => (
              <div key={step.title} className="flex flex-col gap-2">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-strong text-white text-lg font-black">
                  {index + 1}
                </span>
                <h3 className="text-base font-extrabold text-ink">{step.title}</h3>
                <p className="text-sm text-ink-2">{step.text}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-solid border-ink/10">
            {badges.map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-soft text-sm font-bold text-brand-ink"
              >
                <CheckMarkIcon className="w-4 h-4 shrink-0" />
                {badge}
              </span>
            ))}
            {stats?.totalUsers ? (
              <span className="ml-auto text-sm text-ink-2">
                <span className="font-black text-ink">
                  {stats.totalUsers.toLocaleString('uk-UA')}
                </span>{' '}
                {t('clients_found_dream_home')}
              </span>
            ) : null}
          </div>
        </div>
      )}
    </section>
  )
}

export default HowItWorks
